import {useCallback, useEffect, useRef, useState} from "react";
import {capitaliseFirstLetter, maybeAddQuestionMark} from "../services/string";
import {createSpeechRecognition} from "../services/speech-recognitions";

// Custom hook ----

function useSpeechRecognition() {
  const [listeningForSpeech, setListeningForSpeech] = useState(false);
  const [speechText, setSpeechText] = useState<string | null>(null);
  const speechRecognitionRef = useRef<SpeechRecognition | null>(null);

  // Event handlers ----

  const handleResult = useCallback((event: SpeechRecognitionEvent) => {
    let transcript = "";

    for (const result of Object.values(event.results)) {
      transcript += result['0'].transcript;
    }

    setSpeechText(maybeAddQuestionMark(capitaliseFirstLetter(transcript)));
  }, []);

  const handleStart = useCallback(() => {
    setSpeechText(null);
    setListeningForSpeech(true);
  }, []);

  const handleSpeechEnd = useCallback(() => {
    speechRecognitionRef.current?.stop();
  }, []);

  const handleEnd = useCallback(() => {
    setListeningForSpeech(false);
  }, []);

  useEffect(() => {
    // Create the speech recognition instance
    const speechRecognition = createSpeechRecognition();

    // Bail early if speech recognition is not supported
    if (!speechRecognition) return;

    // Store the speech recognition instance in a ref
    speechRecognitionRef.current = speechRecognition;

    // Define event handlers
    speechRecognition.addEventListener('start', handleStart);
    speechRecognition.addEventListener('end', handleEnd);
    speechRecognition.addEventListener('speechend', handleSpeechEnd);
    speechRecognition.addEventListener('result', handleResult);

    // Cleanup event listeners
    return () => {
      speechRecognition.removeEventListener('start', handleStart);
      speechRecognition.removeEventListener('end', handleEnd);
      speechRecognition.removeEventListener('speechend', handleSpeechEnd);
      speechRecognition.removeEventListener('result', handleResult);
    }
  }, [handleEnd, handleResult, handleSpeechEnd, handleStart]);

  // Control helpers ----

  function startSpeechRecognition() {
    speechRecognitionRef.current?.start();
  }

  function stopSpeechRecognition() {
    speechRecognitionRef.current?.stop();
  }

  return { listeningForSpeech, speechText, startSpeechRecognition, stopSpeechRecognition };
}

export { useSpeechRecognition };